import {enumContainer, formattedPropertyModel, NoDataMessageWithIcon, utils} from '@wallaby-ui/core';
import {isEmpty} from 'lodash';
import {Layout, PlotData} from 'plotly.js';
import * as React from 'react';
import {useMemo} from 'react';
import {useIntl} from 'react-intl';

import {useFormatters} from '../../../common';
import {formatLabMethodName, LabResultEventTypeV3Dto} from 'src/js/common/labMethodLabel';
import {MerieSchema, WallabyCoreSchema} from 'src/js/common/types';
import ChartContainer from 'src/js/containers/chart/components/ChartContainer';
import {useAppSelector} from 'src/js/store_hooks';

interface OwnProps {
  singleChartData: WallabyCoreSchema.components['schemas']['LabResultEventChartDataV3Dto'];
  data: WallabyCoreSchema.components['schemas']['EncounterTimelineChartV3Dto'];
  supportedType?: LabResultEventTypeV3Dto;
  phRecommendations?: MerieSchema.components['schemas']['PharmaRecommendationsDto'];
  chartEvents: MerieSchema.components['schemas']['EncounterTimelineChartEventsDto'] | undefined;
}

type Props = OwnProps;

export const DiagnosisLaboratoryResultsItem: React.FC<Props> = (props) => {
  const {data, singleChartData, supportedType, phRecommendations, chartEvents} = props;
  const intl = useIntl();
  const formatters = useFormatters();
  const {t, formatDateShort} = formatters;

  const propDefinition = useMemo(() => {
    return new formattedPropertyModel.FormattedProperty({
      type: formattedPropertyModel.FORMATTED_PROPERTY_TYPE_DECIMAL,
    });
  }, []);

  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);

  const hasData = useMemo(
    () =>
      enumMap.LabResultEventTypeEnum.some(
        (chartEnum) => chartEnum.name === singleChartData.eventType && !isEmpty(singleChartData.dataPoints)
      ),
    [enumMap.LabResultEventTypeEnum, singleChartData.dataPoints, singleChartData.eventType]
  );

  // Data points may arrive out of chronological order; sort ascending by timestamp so the line
  // doesn't jump backwards and the last-value annotation lands on the most recent measurement.
  const sortedDataPoints = useMemo(
    () => [...singleChartData.dataPoints].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
    [singleChartData.dataPoints]
  );

  let formattedPhysiologicalRangeMin: string | undefined;
  let formattedPhysiologicalRangeMax: string | undefined;
  let translatedPhysiologicalRange: string | undefined;

  const labResultEnum = enumMap.LabResultEventTypeEnum.find((enumItem) => {
    return enumItem.name === singleChartData.eventType;
  });

  const translatedLabResultEventTypeEnum =
    labResultEnum?.messageKey && intl && utils.TranslateUtils.translate(labResultEnum?.messageKey, intl);

  if (hasData) {
    formattedPhysiologicalRangeMin =
      singleChartData.physiologicalRangeMin != null
        ? formattedPropertyModel.FormattedProperty.formatValue(propDefinition, singleChartData.physiologicalRangeMin, intl)
        : '-';

    formattedPhysiologicalRangeMax =
      singleChartData.physiologicalRangeMax != null
        ? formattedPropertyModel.FormattedProperty.formatValue(propDefinition, singleChartData.physiologicalRangeMax, intl)
        : '-';

    translatedPhysiologicalRange =
      intl &&
      utils.TranslateUtils.translate('hospitalization.detail.chart.physiologicalRange', intl, {
        rangeMin: formattedPhysiologicalRangeMin,
        rangeMax: formattedPhysiologicalRangeMax,
      });
  }

  const lastDataPointAnnotations = useMemo(() => {
    if (!hasData || isEmpty(sortedDataPoints)) return [];

    const lastPoint = sortedDataPoints[sortedDataPoints.length - 1];

    return [
      {
        x: lastPoint.timestamp,
        y: lastPoint.value,
        text: `${lastPoint.value}`,
        showarrow: false,
        xshift: 10,
        yshift: 0,
        xanchor: 'left',
        yanchor: 'middle',
        bgcolor: 'rgba(255,255,255,0.9)',
        bordercolor: '#636363',
        borderwidth: 1,
        borderpad: 4,
        showlegend: false,
        font: {
          size: 12,
          color: '#000',
        },
      },
    ];
  }, [hasData, sortedDataPoints]);

  const visibleRecommendations = useMemo(() => {
    const chartEventIds = (chartEvents?.pharmaRecommendationEvents || []).map((event) => event.pharmaRecommendationId);
    return phRecommendations?.pharmaRecommendations.filter((rec) => {
      return chartEventIds.includes(rec.id);
    });
  }, [phRecommendations, chartEvents]);

  const pharmaRecommendationShapes = useMemo(() => {
    return (visibleRecommendations || []).map((rec) => {
      const isPublished = rec.lastPublishDate != null;
      const chartEvent = (chartEvents?.pharmaRecommendationEvents || []).filter((event) => event.pharmaRecommendationId === rec.id);
      const displayDate = chartEvent.length > 0 ? chartEvent[0].date : rec.date;

      return {
        type: 'line' as const,
        x0: displayDate,
        x1: displayDate,
        y0: 0,
        y1: 1,
        yref: 'paper' as const,
        line: {
          color: 'black',
          width: 1,
          dash: isPublished ? 'solid' : 'dash',
        },
        showlegend: false,
      };
    });
  }, [visibleRecommendations, chartEvents]);

  const pharmaRecommendationTraces = useMemo<Partial<PlotData>[]>(() => {
    return (visibleRecommendations || []).map((rec) => {
      const isPublished = rec.lastPublishDate != null;
      const chartEvent = (chartEvents?.pharmaRecommendationEvents || []).filter((event) => event.pharmaRecommendationId === rec.id);
      const displayDate = chartEvent.length > 0 ? chartEvent[0].date : rec.date;

      const yValues = singleChartData.dataPoints.map((point) => point.value);
      const yMin = Math.min(...yValues);
      const yMax = Math.max(...yValues);

      return {
        x: [displayDate, displayDate],
        y: [yMin, yMax],

        mode: 'lines' as const,
        type: 'scatter' as const,
        line: {
          color: 'transparent',
          width: 3,
        },
        hovertemplate: `${displayDate ? `${t('filter.recommendation.date')}: ${formatDateShort(displayDate)}` : ''}${
          isPublished
            ? `<br>${t('hospitalization.detail.phRec.lastPublishDate')}: ${rec.lastPublishDate && formatDateShort(rec.lastPublishDate)}`
            : ''
        }<extra></extra>`,
        showlegend: false,
        name: `pharma-recommendation-${rec.id}`,
      } as Partial<PlotData>;
    });
  }, [visibleRecommendations, chartEvents?.pharmaRecommendationEvents, singleChartData.dataPoints, t, formatDateShort]);

  const additionalDataConfig = useMemo<Partial<PlotData>[]>(
    () => [
      {
        hovertemplate: `%{x}: %{y}<br>${translatedPhysiologicalRange}<extra></extra>`,
        showlegend: false,
      },
      ...pharmaRecommendationTraces,
    ],
    [translatedPhysiologicalRange, pharmaRecommendationTraces]
  );

  // Hospital-specific label (from supported-event-types-v3) drives the chart legend per spec:
  //   <hospital label> [<unit>] [<lab method code>]   when label is present
  //   [<lab method code>] [<unit>]                    when label is absent
  // Falls back to chart-v3's unit if the hospital configuration omits a unit.
  const title = formatLabMethodName({
    eventType: singleChartData.eventType,
    label: supportedType?.label,
    unit: supportedType?.unit ?? singleChartData.unit,
  });

  const originalStartDate = new Date(data.startDate);
  const originalEndDate = new Date(data.endDate);

  const startDateWithYearMinus = new Date(originalStartDate);
  startDateWithYearMinus.setFullYear(startDateWithYearMinus.getFullYear() - 1);

  const endDateWithYearPlus = new Date(originalEndDate);
  endDateWithYearPlus.setFullYear(endDateWithYearPlus.getFullYear() + 1);

  const endDateWithDayPlus = new Date(originalEndDate);
  endDateWithDayPlus.setDate(endDateWithDayPlus.getDate() + 1);

  const formattedStartDate = startDateWithYearMinus.toISOString().split('T')[0];
  const formattedEndDate = endDateWithYearPlus.toISOString().split('T')[0];
  const formattedEndRangeDate = endDateWithDayPlus.toISOString().split('T')[0];

  // Compute an explicit y-axis range that always covers data points and (when present) physiologicalRange bounds.
  // This is required because Plotly's autorange would otherwise be pulled by the red-zone shapes if their bounds
  // extended past the data, squashing data points to a flat line.
  const {yaxisMin, yaxisMax} = useMemo(() => {
    const dataValues = singleChartData.dataPoints.map((p) => p.value);
    const candidates = [
      ...dataValues,
      ...(singleChartData.physiologicalRangeMin != null ? [singleChartData.physiologicalRangeMin] : []),
      ...(singleChartData.physiologicalRangeMax != null ? [singleChartData.physiologicalRangeMax] : []),
    ];
    if (candidates.length === 0) return {yaxisMin: 0, yaxisMax: 1};
    const dMin = Math.min(...candidates);
    const dMax = Math.max(...candidates);
    const span = dMax - dMin || Math.abs(dMax) || 1;
    const padding = span * 0.1;
    return {yaxisMin: dMin - padding, yaxisMax: dMax + padding};
  }, [singleChartData.dataPoints, singleChartData.physiologicalRangeMin, singleChartData.physiologicalRangeMax]);

  const labRangeShapes = useMemo(() => {
    const shapes: Record<string, unknown>[] = [];
    const baseRect = {
      type: 'rect',
      xref: 'x',
      yref: 'y',
      x0: formattedStartDate,
      x1: formattedEndDate,
      fillcolor: 'rgba(220, 53, 69, 0.15)',
      line: {width: 0},
      layer: 'below',
    };
    // Extend the red zones far beyond the visible range so the shaded area keeps filling all the way
    // to the plot edge when the user pans/zooms — including below 0. We deliberately ignore
    // minValue/maxValue here; capping at them would stop the fill at the lowest/highest measurable
    // value (e.g. minValue = 0), leaving white space past it.
    const span = yaxisMax - yaxisMin || Math.abs(yaxisMax) || 1;
    const extend = span * 1000;
    const redLowBound = yaxisMin - extend;
    const redHighBound = yaxisMax + extend;
    if (singleChartData.physiologicalRangeMin != null) {
      shapes.push({...baseRect, y0: redLowBound, y1: singleChartData.physiologicalRangeMin});
    }
    if (singleChartData.physiologicalRangeMax != null) {
      shapes.push({...baseRect, y0: singleChartData.physiologicalRangeMax, y1: redHighBound});
    }
    return shapes;
  }, [
    formattedStartDate,
    formattedEndDate,
    singleChartData.physiologicalRangeMin,
    singleChartData.physiologicalRangeMax,
    yaxisMin,
    yaxisMax,
  ]);

  const additionalLayoutConfig = useMemo<Partial<Layout>>(() => {
    return {
      title: {text: title},
      xaxis: {
        range: [data.startDate, formattedEndRangeDate],
        hoverformat: utils.DateUtils.getLocaleD3TimeFormatPattern(intl.locale),
      },
      yaxis: {range: [yaxisMin, yaxisMax]},
      shapes: [...labRangeShapes, ...(pharmaRecommendationShapes || [])] as any,
      annotations: lastDataPointAnnotations as any,
    };
  }, [
    data.startDate,
    formattedEndRangeDate,
    intl.locale,
    title,
    yaxisMin,
    yaxisMax,
    labRangeShapes,
    pharmaRecommendationShapes,
    lastDataPointAnnotations,
  ]);

  return hasData ? (
    <ChartContainer
      reportData={sortedDataPoints}
      chartId={'labResult'}
      configPath={`feature.hospitalizationsDetail.chart.labResult`}
      additionalLayoutConfig={additionalLayoutConfig}
      additionalDataConfig={additionalDataConfig}
      downloadImageTitle={title}
      data-name={`labChart-${singleChartData.eventType}`}
      /* onClick={(plotEvent: PlotMouseEvent) => {
        console.log('clicked data point', plotEvent.points[0]);
      }}
      onClickYTick={(yTick: string) => {
        console.log('clicked yTick', yTick);
      }} */
    />
  ) : (
    <NoDataMessageWithIcon
      key={singleChartData.eventType}
      message={
        intl &&
        utils.TranslateUtils.translate('hospitalization.detail.chart.noData', intl, {
          eventType: title,
        })
      }
      data-name={`labChart-${singleChartData.eventType}`}
    />
  );
};

DiagnosisLaboratoryResultsItem.displayName = 'DiagnosisLaboratoryResultsItem';
